import React, { useState } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import {
  CleanTable,
  CleanTableBody,
  CleanTableCell,
  CleanTableHead,
  CleanTableHeader,
  CleanTableRow,
  CleanTableExpandableRow,
  CleanTableExpandIcon,
  CleanTableExpandedContent,
  useExpandableTable,
} from "../ui/clean-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Plus, Folder, Shield, Edit, Trash2, Eye } from "lucide-react";
import { SubCategoryService } from "../../services/subCategoryService";
import { MetadataService } from "../../services/metadataService";
import EditCategoryForm from "./EditCategoryForm";
import EditSubCategoryForm from "./EditSubCategoryForm";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";

const UnifiedCategoriesManagement = ({
  categories,
  subCategories,
  canManage,
  organizationId,
  onCategoryAdd,
  onSubCategoryAdd,
  onDataUpdated,
  getAccessTokenSilently,
}) => {
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");

  // Edit dialog states
  const [editCategoryOpen, setEditCategoryOpen] = useState(false);
  const [editSubCategoryOpen, setEditSubCategoryOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Use the expandable table hook
  const { isExpanded, toggleRow } = useExpandableTable();

  // Group sub-categories by parent category
  const groupedSubCategories = subCategories.reduce((acc, subCategory) => {
    const categoryId = subCategory.category_id;
    if (!acc[categoryId]) {
      acc[categoryId] = [];
    }
    acc[categoryId].push(subCategory);
    return acc;
  }, {});

  const handleDeleteSubCategory = async (subCategoryId, subCategoryName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the sub-category "${subCategoryName}"?`
      )
    ) {
      return;
    }

    try {
      setLoading(subCategoryId);
      setError("");

      await SubCategoryService.deleteSubCategory(
        subCategoryId,
        getAccessTokenSilently
      );
      onDataUpdated();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading("");
    }
  };

  const getCategoryStats = (categoryId) => {
    const subCategoriesCount = (groupedSubCategories[categoryId] || []).length;
    return { subCategoriesCount };
  };

  // Edit handlers
  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setEditCategoryOpen(true);
  };

  const handleEditSubCategory = (subCategory) => {
    setSelectedSubCategory(subCategory);
    setEditSubCategoryOpen(true);
  };

  const handleCategoryUpdated = () => {
    setEditCategoryOpen(false);
    setSelectedCategory(null);
    onDataUpdated();
  };

  const handleSubCategoryUpdated = () => {
    setEditSubCategoryOpen(false);
    setSelectedSubCategory(null);
    onDataUpdated();
  };

  // Delete handlers
  const handleDeleteCategory = (category) => {
    setDeleteItem(category);
    setDeleteType("category");
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem || !deleteType) return;

    try {
      setDeleting(true);

      if (deleteType === "category") {
        await MetadataService.deleteCategory(
          deleteItem.id,
          getAccessTokenSilently
        );
      }

      setDeleteDialogOpen(false);
      setDeleteItem(null);
      setDeleteType(null);
      onDataUpdated();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteItem(null);
    setDeleteType(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-heading-3">Categories & Sub-Categories</h3>
          <p className="text-body-small text-muted-foreground">
            Manage your hierarchical category structure for organizing controls
          </p>
        </div>
        {canManage && (
          <Button
            size="sm"
            onClick={() => onCategoryAdd()}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Category
          </Button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Categories Table */}
      {categories.length === 0 ? (
        <div className="text-center py-12">
          <Folder className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Categories Found</h3>
          <p className="text-muted-foreground mb-4">
            No categories have been created for this organization yet.
          </p>
          {canManage && (
            <Button
              size="sm"
              onClick={() => onCategoryAdd()}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Your First Category
            </Button>
          )}
        </div>
      ) : (
        <TooltipProvider>
          <CleanTable>
            <CleanTableHeader>
              <CleanTableRow>
                <CleanTableHead className="w-8"></CleanTableHead>
                <CleanTableHead className="w-24">Prefix</CleanTableHead>
                <CleanTableHead>Name</CleanTableHead>
                <CleanTableHead>Description</CleanTableHead>
                <CleanTableHead className="w-32 text-center">
                  Sub-Categories
                </CleanTableHead>
                {canManage && (
                  <CleanTableHead className="w-48 text-right">
                    Actions
                  </CleanTableHead>
                )}
              </CleanTableRow>
            </CleanTableHeader>
            <CleanTableBody>
              {categories.map((category) => {
                const categorySubCategories =
                  groupedSubCategories[category.id] || [];
                const stats = getCategoryStats(category.id);
                const hasSubCategories = categorySubCategories.length > 0;

                return (
                  <React.Fragment key={category.id}>
                    {/* Category Row */}
                    <CleanTableExpandableRow
                      isExpanded={isExpanded(category.id)}
                      onToggle={(e) => {
                        // Prevent expansion when clicking action buttons
                        if (e.target.closest(".action-buttons")) {
                          e.stopPropagation();
                          return;
                        }
                        toggleRow(category.id);
                      }}
                    >
                      <CleanTableExpandIcon
                        isExpanded={isExpanded(category.id)}
                      />
                      <CleanTableCell>
                        <Badge
                          variant="outline"
                          className="text-blue-600 border-blue-600 font-mono"
                        >
                          {category.id_prefix}
                        </Badge>
                      </CleanTableCell>
                      <CleanTableCell>
                        <span className="font-medium text-gray-900">
                          {category.name}
                        </span>
                      </CleanTableCell>
                      <CleanTableCell>
                        <span className="text-gray-900">
                          {category.description || "-"}
                        </span>
                      </CleanTableCell>
                      <CleanTableCell className="text-center">
                        <span className="text-gray-900">
                          {stats.subCategoriesCount}
                        </span>
                      </CleanTableCell>
                      {canManage && (
                        <CleanTableCell className="text-right">
                          <div className="flex items-center justify-end gap-1 action-buttons">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-green-600 hover:text-green-800 hover:bg-green-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSubCategoryAdd(category.id);
                                  }}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Add sub-category</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditCategory(category);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit category</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCategory(category);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete category</TooltipContent>
                            </Tooltip>
                          </div>
                        </CleanTableCell>
                      )}
                    </CleanTableExpandableRow>

                    {/* Expanded Sub-Categories Content */}
                    {isExpanded(category.id) && (
                      <CleanTableExpandedContent colSpan={canManage ? 6 : 5}>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-900">
                              Sub-Categories
                            </h4>
                            {canManage && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onSubCategoryAdd(category.id)}
                                className="flex items-center gap-2"
                              >
                                <Plus className="h-4 w-4" />
                                Add Sub-Category
                              </Button>
                            )}
                          </div>

                          {categorySubCategories.length === 0 ? (
                            <div className="text-center py-6 text-gray-500">
                              <p className="text-sm">
                                No sub-categories in this category yet
                              </p>
                            </div>
                          ) : (
                            <CleanTable>
                              <CleanTableHeader>
                                <CleanTableRow>
                                  <CleanTableHead className="w-24">
                                    Prefix
                                  </CleanTableHead>
                                  <CleanTableHead>Name</CleanTableHead>
                                  <CleanTableHead>Description</CleanTableHead>
                                  {canManage && (
                                    <CleanTableHead className="text-right">
                                      Actions
                                    </CleanTableHead>
                                  )}
                                </CleanTableRow>
                              </CleanTableHeader>
                              <CleanTableBody>
                                {categorySubCategories.map((subCategory) => {
                                  const isDeleting = loading === subCategory.id;

                                  return (
                                    <CleanTableRow key={subCategory.id}>
                                      <CleanTableCell>
                                        <Badge
                                          variant="outline"
                                          className="text-green-600 border-green-600 font-mono"
                                        >
                                          {subCategory.id_prefix}
                                        </Badge>
                                      </CleanTableCell>
                                      <CleanTableCell>
                                        <span className="font-medium text-gray-900">
                                          {subCategory.name}
                                        </span>
                                      </CleanTableCell>
                                      <CleanTableCell>
                                        <span className="text-gray-900">
                                          {subCategory.description || "-"}
                                        </span>
                                      </CleanTableCell>
                                      {canManage && (
                                        <CleanTableCell className="text-right">
                                          <div className="flex items-center justify-end gap-1">
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                                  onClick={() =>
                                                    handleEditSubCategory(
                                                      subCategory
                                                    )
                                                  }
                                                >
                                                  <Edit className="h-4 w-4" />
                                                </Button>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                Edit sub-category
                                              </TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                                                  onClick={() =>
                                                    handleDeleteSubCategory(
                                                      subCategory.id,
                                                      subCategory.name
                                                    )
                                                  }
                                                  disabled={isDeleting}
                                                >
                                                  {isDeleting ? (
                                                    <Spinner size="sm" />
                                                  ) : (
                                                    <Trash2 className="h-4 w-4" />
                                                  )}
                                                </Button>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                Delete sub-category
                                              </TooltipContent>
                                            </Tooltip>
                                          </div>
                                        </CleanTableCell>
                                      )}
                                    </CleanTableRow>
                                  );
                                })}
                              </CleanTableBody>
                            </CleanTable>
                          )}
                        </div>
                      </CleanTableExpandedContent>
                    )}
                  </React.Fragment>
                );
              })}
            </CleanTableBody>
          </CleanTable>
        </TooltipProvider>
      )}

      {/* Edit Forms */}
      <EditCategoryForm
        open={editCategoryOpen}
        onClose={() => setEditCategoryOpen(false)}
        category={selectedCategory}
        onCategoryUpdated={handleCategoryUpdated}
      />

      <EditSubCategoryForm
        open={editSubCategoryOpen}
        onClose={() => setEditSubCategoryOpen(false)}
        subCategory={selectedSubCategory}
        categories={categories}
        onSubCategoryUpdated={handleSubCategoryUpdated}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        loading={deleting}
        itemName={deleteItem?.name}
        itemType={deleteType}
        warningMessage={
          deleteType === "category"
            ? "This will also affect any sub-categories and controls under this category."
            : null
        }
      />
    </div>
  );
};

export default UnifiedCategoriesManagement;
